import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { DEFAULT_TIMEZONE, formatDateInputInTz } from '@/utils/timezone';
import { TechPerformanceMetrics, MetroWithTechs } from '@/types/reports';
import { fetchAllRows, fetchAllRowsIn } from '@/utils/fetchAllRows';

/**
 * techId: undefined = every technician; a string = only that technician's
 * jobs; null = no technician chosen yet, so the query stays idle.
 */
export const useTechReport = (
  startDate: Date,
  endDate: Date,
  timezone: string = DEFAULT_TIMEZONE,
  techId?: string | null
) => {
  return useQuery({
    queryKey: ['reports', 'tech-report', startDate.getTime(), endDate.getTime(), timezone, techId ?? 'all'],
    queryFn: async () => {
      // Use job_date (editable business date) for all filtering —
      // this aligns the overview count with the commission report download.
      const startDateStr = formatDateInputInTz(startDate, timezone);
      const endDateStr = formatDateInputInTz(endDate, timezone);

      // 1. Fetch completed jobs filtered by job_date
      const completedJobs = await fetchAllRows((from, to) => {
        let completedQuery = supabase
          .from('jobs')
          .select(`
            id,
            job_number,
            assigned_tech_id,
            customer_id,
            customer_name,
            scheduled_date,
            job_date,
            invoice_total,
            amount_paid,
            revenue_before_gst,
            payment_status,
            status,
            total_parts_cost,
            toll_cost,
            cancellation_reason,
            cancelled_at,
            metro_id,
            techs (
              id,
              name
            ),
            metros (
              id,
              name
            )
          `)
          .not('assigned_tech_id', 'is', null)
          .eq('status', 'completed')
          .gte('job_date', startDateStr)
          .lte('job_date', endDateStr);
        if (techId) completedQuery = completedQuery.eq('assigned_tech_id', techId);
        return completedQuery.order('id').range(from, to);
      });

      // 2. Fetch cancelled jobs filtered by scheduled_date (cancelled jobs don't have job_date)
      const cancelledJobs = await fetchAllRows((from, to) => {
        let cancelledQuery = supabase
          .from('jobs')
          .select(`
            id,
            job_number,
            assigned_tech_id,
            customer_id,
            customer_name,
            scheduled_date,
            job_date,
            invoice_total,
            amount_paid,
            revenue_before_gst,
            payment_status,
            status,
            total_parts_cost,
            toll_cost,
            cancellation_reason,
            cancelled_at,
            metro_id,
            techs (
              id,
              name
            ),
            metros (
              id,
              name
            )
          `)
          .not('assigned_tech_id', 'is', null)
          .eq('status', 'cancelled')
          .gte('scheduled_date', startDateStr)
          .lte('scheduled_date', endDateStr);
        if (techId) cancelledQuery = cancelledQuery.eq('assigned_tech_id', techId);
        return cancelledQuery.order('id').range(from, to);
      });

      const jobs = [...completedJobs, ...cancelledJobs];

      // 3. Fetch all job payments for these jobs
      const jobIds = jobs?.map(j => j.id) || [];
      if (jobIds.length === 0) {
        return [];
      }

      const payments = await fetchAllRowsIn(jobIds, (ids, from, to) => supabase
        .from('job_payments')
        .select('job_id, amount, payment_method')
        .in('job_id', ids)
        .order('id')
        .range(from, to));

      // 4. Group by metro, then by technician
      const metroMap = new Map<string, {
        metro_id: string;
        metro_name: string;
        techMap: Map<string, TechPerformanceMetrics>;
      }>();

      jobs?.forEach(job => {
        const techId = job.assigned_tech_id;
        const techName = job.techs?.name || 'Unknown';
        const metroId = job.metro_id || 'unassigned';
        const metroName = job.metros?.name || 'Unassigned Metro';

        if (!metroMap.has(metroId)) {
          metroMap.set(metroId, {
            metro_id: metroId,
            metro_name: metroName,
            techMap: new Map()
          });
        }

        const metroEntry = metroMap.get(metroId)!;

        if (!metroEntry.techMap.has(techId)) {
          metroEntry.techMap.set(techId, {
            tech_id: techId,
            tech_name: techName,
            total_jobs: 0,
            closed_jobs: 0,
            canceled_jobs: 0,
            closed_percentage: 0,
            avg_ticket: 0,
            total_parts_used: 0,
            total_tolls: 0,
            cash_payments: 0,
            card_payments: 0,
            bank_transfer_payments: 0,
            total_revenue: 0,
            jobs: [],
            cancelled_jobs_details: []
          });
        }

        const metrics = metroEntry.techMap.get(techId)!;

        metrics.total_jobs++;

        if (job.status === 'completed') {
          metrics.closed_jobs++;
          // Use invoice_total so overview matches the commission report download
          metrics.total_revenue += job.invoice_total || 0;
          metrics.total_parts_used += job.total_parts_cost || 0;
          metrics.total_tolls += job.toll_cost || 0;

          metrics.jobs.push({
            id: job.id,
            job_number: job.job_number,
            customer_id: job.customer_id,
            customer_name: job.customer_name,
            scheduled_date: job.scheduled_date,
            closed_at: job.job_date,
            invoice_total: job.invoice_total,
            payment_status: job.payment_status,
            total_parts_cost: job.total_parts_cost,
            toll_cost: job.toll_cost,
            status: job.status,
            cancellation_reason: null,
            cancelled_at: null,
          });
        }

        if (job.status === 'cancelled') {
          metrics.canceled_jobs++;

          metrics.cancelled_jobs_details.push({
            id: job.id,
            job_number: job.job_number,
            customer_id: job.customer_id,
            customer_name: job.customer_name,
            scheduled_date: job.scheduled_date,
            closed_at: null,
            invoice_total: null,
            payment_status: 'n/a',
            total_parts_cost: 0,
            toll_cost: 0,
            status: job.status,
            cancellation_reason: job.cancellation_reason,
            cancelled_at: job.cancelled_at,
          });
        }

        if (job.status === 'completed') {
          const jobPayments = payments?.filter(p => p.job_id === job.id) || [];
          jobPayments.forEach(payment => {
            if (payment.payment_method === 'cash') {
              metrics.cash_payments += payment.amount;
            } else if (payment.payment_method === 'card') {
              metrics.card_payments += payment.amount;
            } else if (payment.payment_method === 'bank_transfer') {
              metrics.bank_transfer_payments += payment.amount;
            }
          });
        }
      });

      // 5. Calculate percentages and averages, then build result
      const result: MetroWithTechs[] = [];

      metroMap.forEach(metroEntry => {
        const technicians: TechPerformanceMetrics[] = [];

        metroEntry.techMap.forEach(metrics => {
          metrics.closed_percentage = metrics.total_jobs > 0
            ? (metrics.closed_jobs / metrics.total_jobs) * 100
            : 0;
          metrics.avg_ticket = metrics.closed_jobs > 0
            ? metrics.total_revenue / metrics.closed_jobs
            : 0;
          technicians.push(metrics);
        });

        technicians.sort((a, b) => a.tech_name.localeCompare(b.tech_name));

        const metroTotals = technicians.reduce(
          (acc, tech) => ({
            total_jobs: acc.total_jobs + tech.total_jobs,
            closed_jobs: acc.closed_jobs + tech.closed_jobs,
            canceled_jobs: acc.canceled_jobs + tech.canceled_jobs,
            total_revenue: acc.total_revenue + tech.total_revenue,
            total_parts_used: acc.total_parts_used + tech.total_parts_used,
            total_tolls: acc.total_tolls + tech.total_tolls,
            cash_payments: acc.cash_payments + tech.cash_payments,
            card_payments: acc.card_payments + tech.card_payments,
            bank_transfer_payments: acc.bank_transfer_payments + tech.bank_transfer_payments,
          }),
          {
            total_jobs: 0,
            closed_jobs: 0,
            canceled_jobs: 0,
            total_revenue: 0,
            total_parts_used: 0,
            total_tolls: 0,
            cash_payments: 0,
            card_payments: 0,
            bank_transfer_payments: 0,
          }
        );

        result.push({
          metro_id: metroEntry.metro_id,
          metro_name: metroEntry.metro_name,
          technicians,
          ...metroTotals,
          closed_percentage: metroTotals.total_jobs > 0
            ? (metroTotals.closed_jobs / metroTotals.total_jobs) * 100
            : 0,
          avg_ticket: metroTotals.closed_jobs > 0
            ? metroTotals.total_revenue / metroTotals.closed_jobs
            : 0,
        });
      });

      return result.sort((a, b) => {
        if (a.metro_id === 'unassigned') return 1;
        if (b.metro_id === 'unassigned') return -1;
        return a.metro_name.localeCompare(b.metro_name);
      });
    },
    enabled: !!startDate && !!endDate && techId !== null,
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
  });
};
